import { Person, Store } from "@mui/icons-material";
import StoreIcon from "@mui/icons-material/Store";
import { Button, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import Api from "../../api/Api";
import type { Availability, Staff, StoreForReservation } from "../../api/ApiData";
import { useApiContext } from "../../api/ApiProvider";
import infoIcon from "../../img/icon_info.png";
import { palette } from "../../theme";
import type { Action, Func, VoidAction } from "../../util/func";
import useAsyncUpdate from "../../util/useAsyncUpdate";
import useFiles from "../../util/useFile";
import type { ReservationState } from "../../util/useReservationState";
import Breakable from "../Breakable";
import { FloatingUI } from "../FloatingUI";
import FormLabel from "../FormLabel";
import { FormWrapper } from "../FormWrapper";
import Space from "../Space";
import { StyledText } from "../StyledText";
import TimeTable from "../TimeTable/TimeTable";
import getFormParams from "./getFormParams";
import getSource from "./getSource";

export default function Form1({ state }: { state: ReservationState }) {
  const params = getFormParams();
  const source = getSource();
  const { api } = useApiContext();
  const [stores] = useAsyncUpdate(() => api.GetStoresForReservation(params.storeIds, params.courseIds));
  const [availability, setAvailability] = useState<Availability | null>();
  const [paging, setPaging] = useState<number>(0);

  useEffect(() => {
    if (state.data.store) {
      (window as any).dataLayer = [];
      if (state.data.store?.google_tag_manager_id !== "") {
        TagManager.initialize({ gtmId: state.data.store.google_tag_manager_id });
      }
    }
  }, [state.data.store?.id]);

  useEffect(() => {
    setAvailability(undefined);
    if (state.data.store && state.data.course) {
      if (state.data.staff?.unavailable_courses?.includes(state.data.course.id)) {
        state.setValue("course", undefined);
      } else {
        api.GetAvailability(paging, state.data.store, state.data.staff?.id, state.data.course.id).then(setAvailability);
      }
    }
  }, [state.data.staff, state.data.course, paging]);

  useEffect(() => {
    const date = availability?.find((t) => t === params.reservationStartTime);
    if (date) {
      state.setValue("date", date);
    }
  }, [availability]);

  if (!stores) {
    return null;
  }

  const selectingStore = state.data.store;
  const selectingStaff = state.data.staff;

  return (
    <FormWrapper currentIndex={0} skipConfirm={state.data.store?.skip_confirm}>
      <Space h={10} />
      <Stack style={{ gap: 20 }}>
        <StyledSelect errorMessage="利用可能な店舗がありません" getExplanation={(store) => store.message} items={stores} label="ご希望の店舗" setValue={(store) => state.setValue("store", store)} value={state.data.store} />
        {selectingStore && (
          <StaffSelect
            filter={params.filter?.stores.find((s) => s.id === selectingStore.id)?.staffIds}
            setValue={(staff) => {
              state.setValue("staff", staff);
              if (state.data.course && staff?.unavailable_courses.includes(state.data.course.id)) {
                state.setValue("course", undefined);
              }
            }}
            store={selectingStore}
            value={state.data.staff}
          />
        )}
        {selectingStore && selectingStaff !== undefined && (
          <StyledSelect
            errorMessage="利用可能なメニューがありません"
            getExplanation={(course) => course.explanation}
            items={selectingStore.courses
              .filter((c) => params.filter?.stores.find((sf) => sf.id === selectingStore.id)?.courseIds?.includes(c.id) ?? true)
              .filter((c) => {
                if (selectingStaff !== null) {
                  return !selectingStaff.unavailable_courses.includes(c.id);
                } else {
                  return true;
                }
              })}
            label="ご希望のメニュー"
            setValue={(course) => state.setValue("course", course)}
            value={state.data.course}
          />
        )}
        {(() => {
          if (state.data.store && state.data.course && availability) {
            return (
              <Stack>
                <FormLabel icon={<StoreIcon />} required text="ご希望の日時" />
                <Space h={10} />
                <TimeTable availability={availability} onPaging={(page) => setPaging(page)} onSelected={(date) => state.setValue("date", date)} page={paging} store={state.data.store} />
              </Stack>
            );
          }
        })()}
      </Stack>
      <Space h={100} />
      {selectingStore && source === "google" && (
        <Stack style={{ alignItems: "center", background: "white", bottom: 0, boxShadow: "0 0 20px gray", left: 0, padding: "18px 32px", position: "fixed", width: "100vw" }}>
          <Stack flex={1} maxWidth={540} style={{ gap: "6px" }} width={"100%"}>
            <Stack alignItems={"center"} direction={"row"} style={{ fontSize: 15, gap: "10px" }}>
              <Typography flexShrink={0} width={84}>
                選択店舗：
              </Typography>
              <Typography flexShrink={1}>{selectingStore.name}</Typography>
            </Stack>
            <Stack alignItems={"center"} direction={"row"} style={{ gap: "10px" }}>
              <Typography flexShrink={0} width={84}>
                店舗住所：
              </Typography>
              <Typography flexShrink={1}>{`〒${selectingStore.post_code} ${selectingStore.prefecture} ${selectingStore.address_1} ${selectingStore.address_2}`}</Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
    </FormWrapper>
  );
}

function StaffButton({ label, imageUrl, selected, showImage, onclick }: { label: string; imageUrl: string | undefined; selected: boolean; showImage: boolean; onclick: VoidAction }) {
  let img: ReactNode = null;
  if (showImage) {
    if (imageUrl) {
      img = <img alt={imageUrl} src={imageUrl} style={{ height: 90, objectFit: "cover", width: 90 }} />;
    } else {
      img = <Person color="disabled" sx={{ height: 90, width: 75 }} />;
    }
  }

  return (
    <Button color={selected ? "selectedButton" : "unselectedButton"} onClick={onclick} sx={{ border: `2px solid ${selected ? palette.primary.main : "lightgray"}`, minWidth: "auto", p: 1 }} variant="contained">
      <Stack alignItems="center" gap={1}>
        <Typography sx={{ fontSize: ".8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: 90 }}>{label}</Typography>
        {img}
      </Stack>
    </Button>
  );
}

function StyledSelect<T extends { id: number; name: string }>({ label, items, value, setValue, getExplanation, errorMessage }: { label: string; items: T[]; value: T | undefined; setValue: Action<T | undefined>; getExplanation: Func<T, string>; errorMessage: string }) {
  useEffect(() => {
    if (items.length === 1 && items[0].id !== value?.id) {
      setValue(items[0]);
    }
  });

  return (
    <Stack>
      <FormLabel icon={<StoreIcon />} required text={label} />
      <Space h={10} />
      <FormControl fullWidth>
        <Select
          disabled={items.length <= 1}
          displayEmpty
          onChange={(e) => setValue(items.find((item) => item.id.toString() === e.target.value.toString()))}
          renderValue={(selected) => {
            let text = errorMessage;
            if (0 < items.length) {
              text = items.find((item) => item.id.toString() === selected.toString())?.name ?? "";
            }
            return (
              <Typography fontSize="0.8rem" whiteSpace="normal">
                {text}
              </Typography>
            );
          }}
          sx={{ "& .MuiInputBase-input": { padding: "13px 14px" } }}
          value={value?.id?.toString() ?? ""}
        >
          {items.map((item) => (
            <MenuItem key={item.id} sx={{ whiteSpace: "normal" }} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Space h={10} />
      {(() => {
        if (value !== undefined && getExplanation(value) !== "") {
          const explanation = getExplanation(value);
          if (explanation !== "") {
            return (
              <Stack alignItems={"flex-start"} direction={"row"} gap={1}>
                <img alt="info" height={20} src={infoIcon} style={{ flexGrow: 0, flexShrink: 0, marginTop: "1px" }} width={20} />
                <StyledText textStyle={"StoreMessage"}>{explanation}</StyledText>
              </Stack>
            );
          }
        }
      })()}
    </Stack>
  );
}
function StaffSelect({ store, value, setValue, filter }: { store: StoreForReservation; value: Staff | null | undefined; setValue: Action<Staff | null>; filter?: (number | null)[] }) {
  const { api } = useApiContext();
  const { fileUriDict } = useFiles(
    store.staffs.flatMap((s) => s.file_uuid ?? []),
    api,
    [store],
  );

  const showNoNomination = filter === undefined || filter.includes(null);
  const staffs = store.staffs.filter((s) => {
    if (filter === undefined) return true;
    return filter.includes(s.id);
  });

  const [error, setError] = useState<string>();

  useEffect(() => {
    if (value === undefined && error === undefined) {
      if (showNoNomination) {
        setValue(null);
      } else if (0 < staffs.length) {
        setValue(staffs[0]);
      } else {
        setError("選択可能なスタッフがいません。");
      }
    }
  });

  if (error) {
    return <Typography>{error}</Typography>;
  }

  if (!store.nomination_enabled || !fileUriDict || value === undefined) {
    return null;
  }

  const showImage = staffs.some((s) => s.file_uuid !== null);
  return (
    <Stack gap={1}>
      <FormLabel icon={<StoreIcon />} text="ご希望のスタッフ" />
      <Stack direction="row" gap={1} sx={{ overflowX: "auto", scrollbarWidth: "thin" }} whiteSpace="nowrap">
        {showNoNomination && <StaffButton imageUrl={undefined} label="指名なし" onclick={() => setValue(null)} selected={value === null} showImage={showImage} />}
        {staffs.map((s) => (
          <StaffButton imageUrl={s.file_uuid ? fileUriDict[s.file_uuid] : undefined} key={s.id} label={s.nominee_name} onclick={() => setValue(s)} selected={value?.id === s.id} showImage={showImage} />
        ))}
      </Stack>
      <StaffDetail imageUrl={value?.file_uuid ? fileUriDict[value.file_uuid] : undefined} staff={value} />
    </Stack>
  );
}

function StaffDetail({ staff, imageUrl }: { staff: Staff | null; imageUrl: string | undefined }) {
  if (staff === null) {
    return null;
  }

  let img = <></>;
  if (staff.file_uuid) {
    img = <img alt="" src={imageUrl} style={{ flexShrink: 0, height: 164, maxWidth: "25vw", objectFit: "contain", width: 164 }} />;
  }
  return (
    <Stack alignItems="center" direction="row">
      {img}
      <Stack color={"gray"} flexGrow={1} p={1}>
        <Typography color={"black"} fontSize={20}>
          {staff.nominee_name}
        </Typography>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Typography>{staff.nominee_role}</Typography>
          <Stack alignItems="center" direction="row">
            <Typography>指名料:</Typography>
            <Typography color="red" fontSize={20}>
              ￥{staff.nominee_price.toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
        <Stack pt={1} visibility={staff.nominee_message !== "" ? "visible" : "hidden"}>
          <Typography>スタッフからの一言</Typography>
          <Typography>{staff.nominee_message}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
